import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Globe, Layers, ShieldCheck, Zap, Download, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LANGUAGES } from "@/lib/i18n";
import { SAMPLE_MODEL } from "@/lib/sampleModel";
import { AutoBIMLogo, Wordmark } from "@/components/Brand";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import GooglePlayButton from "@/components/GooglePlayButton";
import PlanSVG from "@/components/PlanSVG";
import AdBanner from "@/components/AdBanner";
import AdSenseUnit from "@/components/AdSenseUnit";
import { useAdsConfig } from "@/hooks/useAdsConfig";
import { Button } from "@/components/ui";

export const SUPPORT_EMAIL = "support@autobimlogic.com";
const PLAY_URL = "https://play.google.com/store/apps/details?id=com.autobimlogic.app";

// Premium framed product shot - dark glass frame, soft glow, floating trust
// chips over a clean plan panel. Designed to invite (not intimidate).
function ProductPreview({ caption, chips }) {
  return (
    <div className="relative">
      <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[2rem] bg-primary/20 blur-3xl" />
      <div className="overflow-hidden rounded-2xl border border-primary/30 bg-[#0B0D11] shadow-2xl shadow-primary/20">
        <div className="flex items-center gap-2 border-b border-border bg-[#141821] px-4 py-2.5">
          <span className="h-3 w-3 rounded-full bg-red-400/80" />
          <span className="h-3 w-3 rounded-full bg-amber-400/80" />
          <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
          <span className="ms-3 flex items-center gap-1.5 truncate rounded-md bg-black/40 px-3 py-1 text-[11px] text-muted-foreground">
            <span className="h-2 w-2 rounded-full bg-emerald-400" /> app.autobimlogic.com/editor
          </span>
        </div>
        <div className="relative bg-gradient-to-br from-white to-slate-100 p-2">
          <PlanSVG model={SAMPLE_MODEL} w={720} h={470} showScale showLegend />
          <div className="pointer-events-none absolute inset-0 flex items-start justify-end p-3">
            <div className="rounded-lg bg-black/70 px-2.5 py-1 text-[10px] font-bold text-white backdrop-blur">
              1 : 100 · mm precision
            </div>
          </div>
        </div>
        {caption && <div className="border-t border-border bg-[#141821] px-4 py-2 text-[11px] font-semibold text-muted-foreground">{caption}</div>}
      </div>
      {/* floating trust chips */}
      <div className="absolute -start-3 top-16 hidden rounded-xl border border-emerald-500/40 bg-[#0B0D11]/90 px-3 py-2 shadow-lg backdrop-blur sm:block">
        <div className="flex items-center gap-1.5 text-[11px] font-black text-emerald-400"><ShieldCheck size={13} /> {chips[0]}</div>
      </div>
      <div className="absolute -end-3 bottom-16 hidden rounded-xl border border-primary/40 bg-[#0B0D11]/90 px-3 py-2 shadow-lg backdrop-blur sm:block">
        <div className="flex items-center gap-1.5 text-[11px] font-black text-primary"><Download size={13} /> {chips[1]}</div>
      </div>
    </div>
  );
}

// [emoji, en, ar, es]
const TYPOLOGIES = [
  ["🏡", "Villa", "فيلا", "Villa"], ["🏢", "Apartment", "شقة", "Apartamento"], ["🏨", "Studios", "استوديوهات", "Estudios"],
  ["🏛", "Office", "مكاتب", "Oficina"], ["🛏", "Hotel", "فندق", "Hotel"], ["🛍", "Shops", "محلات", "Tiendas"],
  ["🏬", "Mall", "مول", "Centro comercial"], ["🏊", "Pool", "مسبح", "Piscina"], ["🕌", "Mosque", "مسجد", "Mezquita"],
  ["🏫", "School", "مدرسة", "Escuela"], ["🏥", "Hospital", "مستشفى", "Hospital"], ["🏭", "Factory", "مصنع", "Fábrica"],
  ["🏠", "Luxury Duplex", "دوبلكس فاخر", "Dúplex de lujo"], ["🏬", "Mid-rise Retail", "تجاري متعدد", "Comercial medio"],
];
const EXPORTS = ["DXF", "PDF", "IFC", "XLSX", "SVG"];
const FEATURES = [
  { icon: ShieldCheck, color: "#10B981",
    t: { en: "Zero-Error QA", ar: "جودة صفر أخطاء", es: "Control de calidad sin errores" },
    b: { en: "Every generated plan passes 70+ geometric & structural checks. On failure it auto-retries - you never see a broken drawing.",
         ar: "كل مخطط يخضع لأكثر من 70 فحصاً هندسياً وإنشائياً. عند الفشل يُعاد التوليد تلقائياً - لن ترى مخططاً معطوباً أبداً.",
         es: "Cada plano supera más de 70 comprobaciones geométricas y estructurales. Si falla, se regenera automáticamente - nunca verás un plano roto." } },
  { icon: Zap, color: "#FFD166",
    t: { en: "10 Variants per Project", ar: "10 مخططات لكل مشروع", es: "10 variantes por proyecto" },
    b: { en: "One click generates 10 layouts for the same plot. Pick the one that fits your client - all fully engineered.",
         ar: "بضغطة واحدة نولّد 10 مخططات لنفس القطعة. اختر ما يناسب عميلك - كلها مصممة هندسياً بالكامل.",
         es: "Un clic genera 10 distribuciones para la misma parcela. Elige la que encaje con tu cliente - todas totalmente diseñadas." } },
  { icon: Globe, color: "#4ECDC4",
    t: { en: "15 Languages · Global", ar: "15 لغة · عالمي", es: "15 idiomas · Global" },
    b: { en: "Arabic, English, Spanish, French, German, Turkish, Urdu and more. Full RTL support across the whole product.",
         ar: "العربية والإنجليزية والإسبانية والفرنسية والألمانية والتركية والأردية وأكثر. دعم كامل للاتجاه من اليمين لليسار.",
         es: "Árabe, inglés, español, francés, alemán, turco, urdu y más. Soporte RTL completo en todo el producto." } },
  { icon: Layers, color: "#A78BFA",
    t: { en: "Full BIM Deliverables", ar: "مخرجات BIM كاملة", es: "Entregables BIM completos" },
    b: { en: "Floor plans, elevations, sections, rebar/door/window schedules, MEP layouts, IFC 3D, BOQ - one click, all sheets.",
         ar: "المساقط والواجهات والقطاعات وجداول التسليح والأبواب والنوافذ وMEP والنموذج ثلاثي الأبعاد وجدول الكميات - بضغطة واحدة.",
         es: "Plantas, alzados, secciones, cuadros de armado/puertas/ventanas, MEP, 3D IFC y presupuesto - un clic, todas las láminas." } },
];

const META = {
  en: { t: "AutoBIM Logic - AI-grade Architectural & BIM Plan Generator", d: "Generate complete architectural & structural plans in one click. AutoCAD precision, DXF/PDF/IFC/BOQ, 15 languages." },
  ar: { t: "أوتوبيم لوجيك - مولّد المخططات المعمارية والإنشائية", d: "أنشئ مخططات معمارية وإنشائية كاملة بضغطة واحدة بدقّة أوتوكاد وتصدير DXF/PDF/IFC وبـ15 لغة." },
  es: { t: "AutoBIM Logic - Generador de planos arquitectónicos y BIM con IA", d: "Genera planos arquitectónicos y estructurales completos con un clic. Precisión AutoCAD, DXF/PDF/IFC, 15 idiomas." },
};

// Keyword-rich FAQ - indexable Q&A that also feeds Google's FAQ rich results
// via the JSON-LD injected below. [question{en,ar,es}, answer{en,ar,es}]
const FAQS = [
  {
    q: { en: "What is AutoBIM Logic?", ar: "ما هو أوتوبيم لوجيك؟", es: "¿Qué es AutoBIM Logic?" },
    a: { en: "AutoBIM Logic is an online architectural and structural plan generator. It uses a deterministic, rules-based engineering engine (not guesswork) to produce complete BIM drawings - floor plans, elevations, sections, foundations, MEP layouts and quantity take-offs - for 14 building types in one click.",
         ar: "أوتوبيم لوجيك مولّد مخططات معمارية وإنشائية عبر الإنترنت. يعتمد محرّك قواعد هندسية حتمي (لا تخمين) لإنتاج مخططات BIM كاملة - مساقط وواجهات وقطاعات وأساسات وأنظمة MEP وحصر كميات - لـ14 نوع مبنى بضغطة واحدة.",
         es: "AutoBIM Logic es un generador online de planos arquitectónicos y estructurales. Usa un motor de ingeniería determinista basado en reglas para producir planos BIM completos - plantas, alzados, secciones, cimentaciones, MEP y mediciones - para 14 tipos de edificio con un clic." },
  },
  {
    q: { en: "Which building types can it generate?", ar: "ما أنواع المباني التي يولّدها؟", es: "¿Qué tipos de edificio genera?" },
    a: { en: "Villas, apartments, studios, offices, hotels, shops, malls, pools, mosques, schools, hospitals, factories, luxury duplexes and mid-rise retail - each with a purpose-built generator that follows real engineering rules.",
         ar: "فلل وشقق واستوديوهات ومكاتب وفنادق ومحلات ومولات ومسابح ومساجد ومدارس ومستشفيات ومصانع ودوبلكس فاخر ومبانٍ تجارية متعددة - لكل نوع محرّك مخصّص يتبع قواعد هندسية حقيقية.",
         es: "Villas, apartamentos, estudios, oficinas, hoteles, tiendas, centros comerciales, piscinas, mezquitas, escuelas, hospitales, fábricas, dúplex de lujo y comercio de altura media - cada uno con un generador propio que sigue reglas de ingeniería reales." },
  },
  {
    q: { en: "What file formats can I export?", ar: "ما صيغ الملفات التي يمكن تصديرها؟", es: "¿Qué formatos puedo exportar?" },
    a: { en: "Export DXF for AutoCAD, IFC for Revit and other BIM tools, stamped multi-sheet PDF plan sets, Excel (XLSX) bills of quantities, and SVG - every wall and column is millimetre-accurate.",
         ar: "صدّر DXF لأوتوكاد، وIFC لـ Revit وأدوات BIM، وحزم PDF متعددة الأوراق مختومة، وحصر كميات بصيغة Excel، وSVG - كل جدار وعمود بدقّة المليمتر.",
         es: "Exporta DXF para AutoCAD, IFC para Revit y otras herramientas BIM, juegos de planos PDF de varias láminas, presupuestos en Excel (XLSX) y SVG - cada muro y columna con precisión milimétrica." },
  },
  {
    q: { en: "Which building codes are supported?", ar: "ما أكواد البناء المدعومة؟", es: "¿Qué códigos de construcción admite?" },
    a: { en: "Plans can be checked against the Saudi Building Code (SBC), Eurocode (EN 1992), the International Building Code (IBC) and other regional codes, so setbacks, room sizes and structural spans follow local regulations.",
         ar: "يمكن فحص المخططات وفق كود البناء السعودي (SBC) واليوروكود (EN 1992) والكود الدولي (IBC) وأكواد إقليمية أخرى، لتتبع الارتدادات ومساحات الغرف والبحور الإنشائية الأنظمة المحلية.",
         es: "Los planos se pueden verificar con el Código Saudí (SBC), el Eurocódigo (EN 1992), el International Building Code (IBC) y otros códigos regionales, para que retranqueos, tamaños de sala y luces estructurales cumplan la normativa local." },
  },
  {
    q: { en: "Is AutoBIM Logic free?", ar: "هل أوتوبيم لوجيك مجاني؟", es: "¿AutoBIM Logic es gratis?" },
    a: { en: "Yes - it is currently free for everyone: unlimited projects, 10 variants per project, all 14 building types, and DXF/PDF/IFC/BOQ exports with the automatic zero-error QA layer.",
         ar: "نعم - مجاني حالياً للجميع: مشاريع بلا حدود، و10 مخططات لكل مشروع، وكل الـ14 نوع مبنى، وتصدير DXF/PDF/IFC/BOQ مع طبقة QA الآلية بصفر أخطاء.",
         es: "Sí - actualmente es gratis para todos: proyectos ilimitados, 10 variantes por proyecto, los 14 tipos de edificio y exportaciones DXF/PDF/IFC/BOQ con la capa de QA automática sin errores." },
  },
  {
    q: { en: "Does it support Arabic and other languages?", ar: "هل يدعم العربية ولغات أخرى؟", es: "¿Admite árabe y otros idiomas?" },
    a: { en: "The whole product works in 15 languages with full right-to-left (RTL) support - Arabic, English, Spanish, French, German, Turkish, Urdu, Hindi, Russian, Chinese, Japanese and more.",
         ar: "المنتج بالكامل يعمل بـ15 لغة مع دعم كامل للاتجاه من اليمين إلى اليسار - العربية والإنجليزية والإسبانية والفرنسية والألمانية والتركية والأردية والهندية والروسية والصينية واليابانية وغيرها.",
         es: "Todo el producto funciona en 15 idiomas con soporte RTL completo - árabe, inglés, español, francés, alemán, turco, urdu, hindi, ruso, chino, japonés y más." },
  },
];

function Section({ eyebrow, title, body, children }) {
  return (
    <section className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
      <p className="abl-eyebrow mb-2">{eyebrow}</p>
      <h2 className="max-w-3xl text-2xl font-black leading-tight sm:text-3xl">{title}</h2>
      {body && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{body}</p>}
      {children}
    </section>
  );
}

export default function Landing() {
  const { user, lang } = useAuth();
  const lang3 = ["ar", "en", "es"].includes(lang) ? lang : "en";
  const isAr = lang === "ar";
  // pick(en, ar, es) - Spanish falls back to English if not provided.
  const p = (en, ar, es) => (lang3 === "ar" ? ar : lang3 === "es" ? (es || en) : en);
  const navigate = useNavigate();
  const start = () => navigate(user ? "/studio" : "/register");
  const adsConfig = useAdsConfig();

  useEffect(() => {
    const m = META[lang3] || META.en;
    document.title = m.t;
    const md = document.querySelector('meta[name="description"]');
    if (md) md.setAttribute("content", m.d);

    // Structured data (JSON-LD): SoftwareApplication + FAQPage. Feeds Google
    // rich results and clarifies what the app is for search ranking.
    try {
      const origin = window.location.origin;
      const jsonld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "SoftwareApplication",
            name: "AutoBIM Logic",
            applicationCategory: "DesignApplication",
            operatingSystem: "Web, Android",
            url: origin,
            description: m.d,
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          },
          {
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: (f.q[lang3] || f.q.en),
              acceptedAnswer: { "@type": "Answer", text: (f.a[lang3] || f.a.en) },
            })),
          },
        ],
      };
      let el = document.getElementById("abl-jsonld");
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = "abl-jsonld";
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(jsonld);
    } catch { /* structured data is best-effort */ }
  }, [lang3]);

  return (
    <div className="min-h-screen abl-grid-bg">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/85 px-5 py-3 backdrop-blur">
        <div className="flex items-center gap-2.5">
          <AutoBIMLogo size={34} />
          <div>
            <Wordmark />
            <p className="text-[10px] text-muted-foreground">{p("Automate BIM · Engineer with Logic", "أتمتة الـBIM بمنطق هندسي", "Automatiza BIM · Diseña con lógica")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/showcase" className="hidden sm:block"><Button variant="ghost" size="sm" data-testid="landing-showcase">{p("Showcase", "المعرض", "Galería")}</Button></Link>
          <LanguageSwitcher compact />
          {!user && (
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="landing-signin">{p("Sign in", "دخول", "Entrar")}</Button>
            </Link>
          )}
          <Button size="sm" onClick={start} data-testid="landing-cta-header">
            {user ? p("Studio", "الاستوديو", "Estudio") : p("Try free", "جرّب مجاناً", "Probar gratis")}
            <ArrowRight size={14} />
          </Button>
        </div>
      </header>

      {/* Admin-managed promo banners (public) */}
      <div className="mx-auto max-w-6xl px-5 pt-3"><AdBanner variant="public" isAr={isAr} /></div>

      {/* HERO */}
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-14 lg:grid-cols-2 lg:py-20">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-[11px] font-bold text-emerald-400">
            <Sparkles size={13} /> {p("14 building types · AutoCAD-grade", "14 نوع مبنى · بدقة CAD", "14 tipos de edificio · nivel AutoCAD")}
          </span>
          <h1 className="mt-4 text-4xl font-black leading-[1.1] sm:text-5xl lg:text-6xl">
            {p("Complete architectural plans in one click.", "خطط معمارية كاملة بضغطة واحدة.", "Planos arquitectónicos completos con un clic.")}
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
            {p(
              "Villa · Apartment · Mosque · School · Hospital · Factory · Hotel · Mall - 14 building types with DXF / PDF / IFC / BOQ exports.",
              "فيلا · شقة · مسجد · مدرسة · مستشفى · مصنع · فندق · مول - 14 نوع مبنى مع تصدير DXF / PDF / IFC / BOQ.",
              "Villa · Apartamento · Mezquita · Escuela · Hospital · Fábrica · Hotel · Centro comercial - 14 tipos con exportación DXF / PDF / IFC / BOQ.")}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button size="lg" onClick={start} data-testid="hero-cta-primary">
              {user ? p("Open Studio", "افتح الاستوديو", "Abrir Estudio") : p("Start free - no card", "ابدأ مجاناً - بدون بطاقة", "Empieza gratis - sin tarjeta")}
              <ArrowRight size={18} />
            </Button>
            <GooglePlayButton href={PLAY_URL} />
          </div>
          <div className="mt-3">
            <Link to={user ? "/projects" : "/login"}>
              <Button size="lg" variant="outline">{user ? p("Projects", "المشاريع", "Proyectos") : p("I have an account", "لديّ حساب", "Ya tengo cuenta")}</Button>
            </Link>
          </div>
          <div className="mt-6 space-y-1.5">
            {[
              p("Free for everyone - now", "مجاني للجميع الآن", "Gratis para todos - ahora"),
              p("AutoCAD-grade precision", "بدقة AutoCAD الهندسية", "Precisión nivel AutoCAD"),
              p("15 languages · full RTL", "15 لغة · دعم RTL كامل", "15 idiomas · RTL completo"),
            ].map((b) => (
              <div key={b} className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 size={15} className="text-emerald-400" /> {b}
              </div>
            ))}
          </div>
        </div>
        <div>
          <ProductPreview
            caption={p(
              "A real villa plan generated by AutoBIM Logic - walls, rooms, doors, windows & columns at millimetre precision",
              "مخطط فيلا حقيقي مُولّد بواسطة AutoBIM Logic - جدران وغرف وأبواب ونوافذ وأعمدة بدقة المليمتر",
              "Un plano de villa real generado por AutoBIM Logic - muros, habitaciones, puertas, ventanas y columnas con precisión milimétrica")}
            chips={[
              p("Zero-Error QA", "جودة صفر أخطاء", "QA sin errores"),
              p("DXF · PDF · IFC", "DXF · PDF · IFC", "DXF · PDF · IFC"),
            ]}
          />
        </div>
      </section>

      <Section eyebrow={p("Building types", "أنواع المباني", "Tipos de edificio")} title={p("14 typologies - engineered end-to-end", "14 نوع مبنى - مصممة هندسياً بالكامل", "14 tipologías - diseñadas de principio a fin")}
        body={p("From tiny villas to full hospitals - every typology has a purpose-built generator with strict engineering rules.", "من الفيلا الصغيرة إلى المستشفى والمصنع، لكل نوع مولّد مصمم خصيصاً بمعايير هندسية دقيقة.", "Desde villas pequeñas hasta hospitales - cada tipología tiene un generador dedicado con reglas de ingeniería estrictas.")}>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {TYPOLOGIES.map(([e, en, ar, es], i) => (
            <div key={i} className="abl-card flex flex-col items-center gap-2 p-4 transition-transform hover:-translate-y-1">
              <span className="text-3xl">{e}</span>
              <span className="text-center text-xs font-bold">{p(en, ar, es)}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow={p("Why AutoBIM Logic", "لماذا AutoBIM Logic", "Por qué AutoBIM Logic")} title={p("Built by engineers, feels like AutoCAD", "مصمم للمهندسين، بحس AutoCAD", "Hecho por ingenieros, se siente como AutoCAD")}>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {FEATURES.map((f, i) => (
            <div key={i} className="abl-card p-5">
              <div className="mb-3 grid h-12 w-12 place-items-center rounded-xl" style={{ background: `${f.color}22` }}>
                <f.icon size={22} color={f.color} />
              </div>
              <h3 className="text-base font-black">{f.t[lang3] || f.t.en}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.b[lang3] || f.b.en}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow={p("Global reach", "الوصول العالمي", "Alcance global")} title={p("15 languages · RTL first-class", "15 لغة · تدعم اليمين لليسار أولاً", "15 idiomas · RTL de primera clase")}>
        <div className="mt-5 flex flex-wrap gap-2">
          {LANGUAGES.map((l) => (
            <span key={l.code} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/60 px-3 py-1.5 text-sm font-semibold">
              <span>{l.flag}</span> {l.nativeName}
            </span>
          ))}
        </div>
      </Section>

      <Section eyebrow={p("Exports", "التصدير", "Exportación")} title={p("5 industry formats - one click", "5 صيغ صناعية - بضغطة واحدة", "5 formatos del sector - un clic")}
        body={p("Open the DXF straight in AutoCAD or Revit - every wall and column is millimetre-accurate.", "افتح الـDXF مباشرة في AutoCAD أو Revit - كل جدار وعمود بأبعاده الحقيقية بالمليمتر.", "Abre el DXF directamente en AutoCAD o Revit - cada muro y columna es preciso al milímetro.")}>
        <div className="mt-5 flex flex-wrap gap-3">
          {EXPORTS.map((f) => (
            <span key={f} className="inline-flex items-center gap-2 rounded-lg border border-border bg-card/60 px-4 py-2 font-black">
              <Download size={16} className="text-primary" /> {f}
            </span>
          ))}
        </div>
      </Section>

      <Section eyebrow={p("FAQ", "الأسئلة الشائعة", "Preguntas frecuentes")}
        title={p("Everything you need to know", "كل ما تحتاج معرفته", "Todo lo que necesitas saber")}
        body={p("Common questions about the architectural & structural plan generator, exports and building codes.", "أسئلة شائعة حول مولّد المخططات المعمارية والإنشائية والتصدير وأكواد البناء.", "Preguntas frecuentes sobre el generador de planos arquitectónicos y estructurales, exportaciones y códigos de construcción.")}>
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          {FAQS.map((f, i) => (
            <div key={i} className="rounded-xl border border-border bg-card/60 p-5" data-testid={`faq-item-${i}`}>
              <h3 className="text-base font-black">{f.q[lang3] || f.q.en}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a[lang3] || f.a.en}</p>
            </div>
          ))}
        </div>
      </Section>

      <section className="mx-auto max-w-6xl px-5 pb-16">
        <AdSenseUnit config={adsConfig} slotKey="landing" style={{ marginBottom: 24 }} />
        <div className="rounded-2xl border-2 border-primary bg-card/60 p-8">
          <span className="rounded-md bg-primary px-2.5 py-1 text-[10px] font-black text-primary-foreground">{p("FREEMIUM MODE ACTIVE", "الوضع المجاني مُفعّل", "MODO FREEMIUM ACTIVO")}</span>
          <h3 className="mt-3 text-xl font-black">{p("Unlimited projects · Unlimited exports", "أنشئ بلا حدود، صدّر بلا حدود", "Proyectos ilimitados · Exportaciones ilimitadas")}</h3>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {[
              p("All 14 building types", "كل الـ14 نوع مبنى", "Los 14 tipos de edificio"),
              p("10 variants per project", "توليد 10 مخططات لكل مشروع", "10 variantes por proyecto"),
              p("DXF · PDF · IFC · BOQ exports", "تصدير DXF · PDF · IFC · BOQ", "Exportar DXF · PDF · IFC · BOQ"),
              p("Automatic QA layer (0% errors)", "طبقة QA الآلية (0% أخطاء)", "Capa de QA automática (0% errores)"),
            ].map((b) => (
              <div key={b} className="flex items-center gap-2 text-sm font-semibold">
                <CheckCircle2 size={16} className="text-emerald-400" /> {b}
              </div>
            ))}
          </div>
          <Button className="mt-6" size="lg" onClick={start} data-testid="pricing-cta">
            {user ? p("Open Projects", "افتح المشاريع", "Abrir Proyectos") : p("Create free account", "أنشئ حساباً مجانياً", "Crear cuenta gratis")}
            <ArrowRight size={18} />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border bg-[#0B0D11]">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2.5"><AutoBIMLogo size={32} /><Wordmark /></div>
            <p className="mt-3 max-w-xs text-sm text-muted-foreground">{p("A global platform for generating architectural & structural plans with AutoCAD-grade precision.", "منصّة عالمية لتوليد المخططات المعمارية والإنشائية بدقة AutoCAD.", "Una plataforma global para generar planos arquitectónicos y estructurales con precisión nivel AutoCAD.")}</p>
            <div className="mt-4"><GooglePlayButton href={PLAY_URL} /></div>
          </div>
          <div>
            <p className="mb-3 text-sm font-black">{p("Product", "المنتج", "Producto")}</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/register" className="hover:text-foreground">{p("Get started", "ابدأ مجاناً", "Empezar")}</Link></li>
              <li><Link to="/login" className="hover:text-foreground">{p("Sign in", "تسجيل الدخول", "Entrar")}</Link></li>
              <li><Link to="/showcase" className="hover:text-foreground" data-testid="footer-showcase">{p("Showcase", "المعرض", "Galería")}</Link></li>
              <li><Link to="/methodology" className="hover:text-foreground" data-testid="footer-methodology">{p("How it works", "كيف يعمل", "Cómo funciona")}</Link></li>
              <li><Link to="/contact" className="hover:text-foreground" data-testid="footer-contact">{p("Contact", "تواصل معنا", "Contacto")}</Link></li>
              <li><a href={PLAY_URL} target="_blank" rel="noreferrer" className="hover:text-foreground">{p("Android app", "تطبيق أندرويد", "App de Android")}</a></li>
            </ul>
          </div>
          <div>
            <p className="mb-3 text-sm font-black">{p("Legal", "قانوني", "Legal")}</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/legal/privacy" className="hover:text-foreground" data-testid="footer-privacy">{p("Privacy Policy", "سياسة الخصوصية", "Política de privacidad")}</Link></li>
              <li><Link to="/legal/terms" className="hover:text-foreground" data-testid="footer-terms">{p("Terms of Service", "الشروط والأحكام", "Términos del servicio")}</Link></li>
            </ul>
          </div>
          <div>
            <p className="mb-3 text-sm font-black">{p("Contact us", "تواصل معنا", "Contáctanos")}</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-foreground" data-testid="footer-support-email">{SUPPORT_EMAIL}</a></li>
              <li>{p("24/7 support", "الدعم الفني على مدار الساعة", "Soporte 24/7")}</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border py-5 text-center text-xs text-muted-foreground">
          © 2026 AutoBIM Logic. {p("All rights reserved.", "جميع الحقوق محفوظة.", "Todos los derechos reservados.")} · Automate BIM. Engineer with Logic.
        </div>
      </footer>
    </div>
  );
}
